import axios from 'axios';

// Optional: directoryConfig.chat.endpoint. Else in development use
// directoryConfig.chat.CHAT_API_STAGE_HYBRID_BASE; prod builds use same-origin unless overridden.
function getChatApiStageHybridBase() {
  const base = window.directoryConfig?.chat?.CHAT_API_STAGE_HYBRID_BASE;
  return base || '';
}

function getChatApiKey() {
  return window.directoryConfig?.chat?.apiKey || window.directoryConfig?.adobeIO?.apiKey || '';
}

const CHAT_API_BASE = getChatApiStageHybridBase();

const chatClient = axios.create({
  baseURL: CHAT_API_BASE,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-api-key': getChatApiKey(),
  },
});

function normalizeSuggestions(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
}

/**
 * Response shape from POST /query.
 * @typedef {{
 *   response: string,
 *   query: string,
 *   session_id: string,
 *   suggestions: string[],
 * }} QueryResponse
 */

/**
 * Send a query to the chat API. Use the same session_id on follow-ups to keep context.
 * @param {string} query - Natural language question (non-empty).
 * @param {string|null} [sessionId=null] - Session ID for follow-ups; null = new conversation.
 * @param {string|null} [primaryBusiness=null] - Partner program tab the chat was opened
 *   from ('Solution' or 'Technology'); used to scope directory searches by default.
 * @returns {Promise<QueryResponse>} Query result; `response` is Markdown.
 */
export async function sendQuery(query, sessionId = null, primaryBusiness = null) {
  const body = { query };
  if (sessionId != null && sessionId !== '') {
    body.session_id = sessionId;
  }
  if (primaryBusiness != null && primaryBusiness !== '') {
    body.primaryBusiness = primaryBusiness;
  }
  try {
    const { data } = await chatClient.post('/query', body);
    return {
      response: data?.response != null ? String(data.response) : '',
      query: data?.query != null ? String(data.query) : query,
      session_id: data?.session_id != null ? String(data.session_id) : '',
      suggestions: normalizeSuggestions(data?.suggestions),
    };
  } catch (err) {
    const detail = err.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : err.message || 'Something went wrong.';
    throw new Error(message);
  }
}

/**
 * Check if the chat API is up.
 * @returns {Promise<boolean>}
 */
export async function checkHealth() {
  try {
    const { data } = await chatClient.get('/health');
    return data?.status === 'ok';
  } catch {
    return false;
  }
}

/**
 * Clear conversation history for a session on the server.
 * @param {string} sessionId
 * @returns {Promise<void>}
 */
export async function clearSession(sessionId) {
  if (!sessionId) return;
  await chatClient.delete(`/session/${encodeURIComponent(sessionId)}`);
}

export default chatClient;
